import {createFeatureSelector, createSelector, MemoizedSelector} from '@ngrx/store';
import {DATASET_METRICS_STATE_NAME, DataSetMetricsState} from './dataSetMetrics';
import {Serializers} from '../../interfaces/serializer';
import {DataSetMetric} from '../../interfaces/metrics';
import {MetricComponentObject, MetricComponentObjects} from '../../classes/metric-component-object';

export const selectDataSetMetricsState:MemoizedSelector<object, DataSetMetricsState> = createFeatureSelector<DataSetMetricsState>(DATASET_METRICS_STATE_NAME);

export const selectDataSetMetrics: MemoizedSelector<object, Serializers<DataSetMetric>> = createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): Serializers<DataSetMetric> => state.serialized.all
);

export const selectDataSetMetricsNormalized: MemoizedSelector<object, Serializers<DataSetMetric>> = createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): Serializers<DataSetMetric> => state.serialized.normalized
);

export const selectDataSetMetricsSource: MemoizedSelector<object, Serializers<DataSetMetric>> = createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): Serializers<DataSetMetric> => state.serialized.source
);

export const selectDataSetMetricsCalculated: MemoizedSelector<object, Serializers<DataSetMetric>> = createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): Serializers<DataSetMetric> => state.serialized.calculated
);

export const selectDataSetMetricComponentObjects: MemoizedSelector<object, MetricComponentObjects> = createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): MetricComponentObjects => state.componentObjects.all
);

export const selectDataSetMetricNormalizedComponentObjects: MemoizedSelector<object, MetricComponentObjects> = createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): MetricComponentObjects => state.componentObjects.normalized
);

export const selectDataSetMetricSourceComponentObjects: MemoizedSelector<object, MetricComponentObjects> = createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): MetricComponentObjects => state.componentObjects.source
);

export const selectDataSetMetricCalculatedComponentObjects: MemoizedSelector<object, MetricComponentObjects> = createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): MetricComponentObjects => state.componentObjects.calculated
);

export const selectDataSetMetricsSourceComponentObjectsByDataSetUsageID: (id: number) => MemoizedSelector<object, MetricComponentObjects> = (id: number): MemoizedSelector<object, MetricComponentObjects> => createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): MetricComponentObjects => state.componentObjects.source.filter(componentObject => componentObject.payload.relationships.data_source.data.id == id)
);

export const selectDataSetMetricsLoading: MemoizedSelector<object, boolean> = createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): boolean => state.loading
);

export const selectDataSetMetricsLoaded: MemoizedSelector<object, boolean> = createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): boolean => state.loaded
);

export const selectDataSetMetricAttributedMetricsComponentObjects: MemoizedSelector<object, MetricComponentObjects> = createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): MetricComponentObjects => state.componentObjects.all.filter((metric: MetricComponentObject): boolean =>
    metric.payload.relationships.data_source_metric.data &&
    ['conversion', 'main_conversion'].includes(metric.payload.relationships.data_source_metric.data.attributes?.metric_type) &&
    !metric.payload.relationships.data_source_metric.data.attributes.slug.includes('revenue')).reduce((output: MetricComponentObjects, currentMetric: MetricComponentObject): MetricComponentObjects => {
      const metric: MetricComponentObject = state.componentObjects.all.find((met: MetricComponentObject): boolean => currentMetric.payload.attributes.slug !== met.payload.attributes.slug && currentMetric.payload.attributes.slug.includes(met.payload.attributes.slug));

      if (metric && !output.includes(metric)) {
        output.push(metric);
      }

      return output;
    }, [] as MetricComponentObjects)
);

export const selectDataSetMetricConversionMetricsComponentObjects: MemoizedSelector<object, MetricComponentObjects> = createSelector(
  selectDataSetMetricsState,
  (state: DataSetMetricsState): MetricComponentObjects => state.componentObjects.all.filter((metric: MetricComponentObject): boolean => ['conversion', 'main_conversion'].includes((metric.payload.attributes as DataSetMetric).metric_type))
);
